import React, { useState, useEffect } from 'react';
import { 
  Download, 
  TrendingUp, 
  AlertTriangle, 
  CheckCircle,
  FileText,
  Mail,
  Clock,
  User,
  Building,
  MapPin,
  Target,
  Award,
  AlertCircle
} from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { analysisAPI, downloadFile } from '../services/api';

const AnalysisResults = ({ analysisId, onBack }) => {
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [downloadLoading, setDownloadLoading] = useState('');

  useEffect(() => {
    fetchAnalysis();
  }, [analysisId]);

  const fetchAnalysis = async () => {
    try {
      const data = await analysisAPI.getAnalysis(analysisId);
      setAnalysis(data);
    } catch (err) {
      setError('Failed to load analysis results');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (type) => {
    setDownloadLoading(type);
    try {
      let blob, filename;
      
      switch (type) {
        case 'report':
          blob = await analysisAPI.downloadReport(analysisId);
          filename = 'resume_analysis_report.pdf';
          break;
        case 'suggestions':
          blob = await analysisAPI.downloadSuggestions(analysisId);
          filename = 'resume_suggestions.docx';
          break;
        case 'cover-letter':
          blob = await analysisAPI.downloadCoverLetter(analysisId);
          filename = 'cover_letter.docx';
          break;
        default:
          return;
      }
      
      downloadFile(blob, filename);
    } catch (err) {
      console.error('Download failed:', err);
    } finally {
      setDownloadLoading('');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'text-green-600 bg-green-100';
      case 'processing': return 'text-blue-600 bg-blue-100';
      case 'partial': return 'text-orange-600 bg-orange-100';
      case 'failed': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getMatchScoreColor = (score) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/3"></div>
          <div className="h-32 bg-gray-200 rounded"></div>
          <div className="h-48 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <Card className="max-w-2xl mx-auto">
        <CardContent className="text-center py-8">
          <AlertCircle className="mx-auto h-12 w-12 text-red-500 mb-4" />
          <p className="text-gray-600 mb-4">{error}</p>
          <Button onClick={onBack}>Back to Dashboard</Button>
        </CardContent>
      </Card>
    );
  }

  const { match_analysis, parsed_resume, parsed_job_description, ats_check, cover_letter, status, processing_errors } = analysis;

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Analysis Results</h1>
          <p className="text-gray-600">
            Created: {new Date(analysis.created_at).toLocaleDateString()}
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(status)}`}>
            {status.charAt(0).toUpperCase() + status.slice(1)}
          </span>
          <Button variant="outline" onClick={onBack}>
            Back to Dashboard
          </Button>
        </div>
      </div>

      {/* Processing Errors */}
      {processing_errors && processing_errors.length > 0 && (
        <Card className="border-orange-200 bg-orange-50">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2 text-orange-800">
              <AlertTriangle className="h-5 w-5" />
              <span>Processing Issues</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="text-sm text-orange-700 space-y-1">
              {processing_errors.map((error, index) => (
                <li key={index}>• {error}</li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* Match Score Overview */}
      {match_analysis && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <TrendingUp className="h-6 w-6 text-blue-600" />
              <span>Match Analysis</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-3xl font-bold text-gray-900">
                  <span className={getMatchScoreColor(match_analysis.match_score)}>
                    {match_analysis.match_score}%
                  </span>
                </h3>
                <p className="text-gray-600">Overall Match Score</p>
              </div>
              <div className="text-right">
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div className="text-center">
                    <p className="font-semibold text-green-600">{match_analysis.strengths?.length || 0}</p>
                    <p className="text-gray-500">Strengths</p>
                  </div>
                  <div className="text-center">
                    <p className="font-semibold text-orange-600">{match_analysis.gaps?.length || 0}</p>
                    <p className="text-gray-500">Gaps</p>
                  </div>
                  <div className="text-center">
                    <p className="font-semibold text-red-600">{match_analysis.missing_skills?.length || 0}</p>
                    <p className="text-gray-500">Missing Skills</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Job Information */}
            {parsed_job_description && (
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <h4 className="font-semibold text-gray-900 mb-2">Target Position</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                  {parsed_job_description.job_title && (
                    <div className="flex items-center space-x-2">
                      <User className="h-4 w-4 text-gray-400" />
                      <span>{parsed_job_description.job_title}</span>
                    </div>
                  )}
                  {parsed_job_description.company && (
                    <div className="flex items-center space-x-2">
                      <Building className="h-4 w-4 text-gray-400" />
                      <span>{parsed_job_description.company}</span>
                    </div>
                  )}
                  {parsed_job_description.location && (
                    <div className="flex items-center space-x-2">
                      <MapPin className="h-4 w-4 text-gray-400" />
                      <span>{parsed_job_description.location}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Strengths */}
        {match_analysis?.strengths && match_analysis.strengths.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2 text-green-600">
                <CheckCircle className="h-5 w-5" />
                <span>Key Strengths</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2">
                {match_analysis.strengths.slice(0, 5).map((strength, index) => (
                  <li key={index} className="flex items-start space-x-2">
                    <Award className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-gray-700">{strength}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}

        {/* Gaps */}
        {match_analysis?.gaps && match_analysis.gaps.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2 text-orange-600">
                <Target className="h-5 w-5" />
                <span>Areas for Improvement</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2">
                {match_analysis.gaps.slice(0, 5).map((gap, index) => (
                  <li key={index} className="flex items-start space-x-2">
                    <AlertTriangle className="h-4 w-4 text-orange-500 mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-gray-700">{gap}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Missing Skills */}
      {match_analysis?.missing_skills && match_analysis.missing_skills.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2 text-red-600">
              <AlertCircle className="h-5 w-5" />
              <span>Missing Skills</span>
            </CardTitle>
            <CardDescription>
              Skills mentioned in the job description that weren't found in your resume
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {match_analysis.missing_skills.slice(0, 15).map((skill, index) => (
                <span 
                  key={index}
                  className="px-3 py-1 bg-red-50 text-red-700 rounded-full text-sm border border-red-200"
                >
                  {skill}
                </span>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* ATS Issues */}
      {ats_check?.ats_issues && ats_check.ats_issues.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2 text-orange-600">
              <AlertTriangle className="h-5 w-5" />
              <span>ATS Compliance Issues</span>
            </CardTitle>
            <CardDescription>
              Issues that might prevent your resume from being properly read by ATS systems
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {ats_check.ats_issues.slice(0, 5).map((issue, index) => (
                <div key={index} className="border-l-4 border-orange-400 pl-4">
                  <h4 className="font-semibold text-gray-900">{issue.issue}</h4>
                  <p className="text-sm text-gray-600 mb-2">{issue.why_it_matters}</p>
                  <p className="text-sm text-green-700 bg-green-50 p-2 rounded">
                    <strong>Fix:</strong> {issue.fix}
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recommendations */}
      {match_analysis?.recommendations && match_analysis.recommendations.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2 text-blue-600">
              <FileText className="h-5 w-5" />
              <span>Specific Recommendations</span>
            </CardTitle>
            <CardDescription>
              AI-generated suggestions to improve your resume for this position
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {match_analysis.recommendations.map((rec, index) => (
                <div key={index} className="flex items-start space-x-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-sm font-medium">
                    {index + 1}
                  </span>
                  <p className="text-sm text-gray-700">{rec}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Cover Letter Preview */}
      {cover_letter && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2 text-purple-600">
              <Mail className="h-5 w-5" />
              <span>Generated Cover Letter</span>
            </CardTitle>
            <CardDescription>
              AI-generated cover letter tailored to this position
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="bg-gray-50 rounded-lg p-4 max-h-64 overflow-y-auto">
              <pre className="whitespace-pre-wrap text-sm text-gray-700 font-sans">
                {cover_letter}
              </pre>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Download Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Download className="h-6 w-6 text-blue-600" />
            <span>Download Reports</span>
          </CardTitle>
          <CardDescription>
            Download your analysis results in various formats
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Button
              variant="outline"
              onClick={() => handleDownload('report')}
              disabled={downloadLoading === 'report'}
              className="flex items-center space-x-2"
            >
              {downloadLoading === 'report' ? (
                <Clock className="h-4 w-4 animate-spin" />
              ) : (
                <FileText className="h-4 w-4" />
              )}
              <span>Analysis Report (PDF)</span>
            </Button>

            <Button
              variant="outline"
              onClick={() => handleDownload('suggestions')}
              disabled={downloadLoading === 'suggestions'}
              className="flex items-center space-x-2"
            >
              {downloadLoading === 'suggestions' ? (
                <Clock className="h-4 w-4 animate-spin" />
              ) : (
                <FileText className="h-4 w-4" />
              )}
              <span>Suggestions (DOCX)</span>
            </Button>

            {cover_letter && (
              <Button
                variant="outline"
                onClick={() => handleDownload('cover-letter')}
                disabled={downloadLoading === 'cover-letter'}
                className="flex items-center space-x-2"
              >
                {downloadLoading === 'cover-letter' ? (
                  <Clock className="h-4 w-4 animate-spin" />
                ) : (
                  <Mail className="h-4 w-4" />
                )}
                <span>Cover Letter (DOCX)</span>
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AnalysisResults;